/**
 * The copy's sealing, off the page (decision 75; part 38b log, DI4, DI6): one item's plaintext sealed
 * under a fresh copy key and that key wrapped under each holder's person key, in a worker of its own, so the reconcile
 * never holds the page while it encrypts. The messages each side receives are read field by field (Definition of Done,
 * security): a message that isn't one is never taken for one. The worker and WebCrypto are injected, so the logic runs in
 * tests; where no worker starts, the page seals it itself (the same function), still never on a keystroke.
 */
import { COPY_SEAL_BYTES, COPY_VERSION_BYTES, COPY_WRAP_BYTES, INVITE_ID_BYTES, INVITE_KEY_BYTES, sealCopy, wrapCopyKey } from '@cockatrice/crypto';
import type { WorkerSource } from '../preview/platform.ts';
import type { Random } from './secrets.ts';

/** The most plaintext a job carries (part 38a's `MAX_COPY_BYTES`). */
const MAX_PLAIN = 1024 * 1024;

/** The most holders a job names (part 38a's `MAX_INVITES_PER_SHARE`). */
const MAX_HOLDERS = 50;

/** An item's sealing: where it belongs, its plaintext, and each holder's invite ID and person key. */
export interface SealJob {
  readonly kind: 'seal';
  readonly share: Uint8Array<ArrayBuffer>;
  readonly item: string;
  readonly version: Uint8Array<ArrayBuffer>;
  readonly plain: Uint8Array<ArrayBuffer>;
  readonly holders: readonly { readonly invite: Uint8Array<ArrayBuffer>; readonly key: Uint8Array<ArrayBuffer> }[];
}

/** A sealed item: its bytes and each holder's wrap, in the job's order. */
export interface SealedCopy {
  readonly bytes: Uint8Array<ArrayBuffer>;
  readonly wraps: readonly Uint8Array<ArrayBuffer>[];
}

/** The worker's answer: the sealed item, or a failure. */
export type SealAnswer = { readonly kind: 'sealed'; readonly copy: SealedCopy } | { readonly kind: 'failed' };

/** DI4: `job`'s item sealed under a fresh copy key from `random`, wrapped for each holder, each with a fresh IV. */
export async function sealItem(subtle: SubtleCrypto, random: Random, job: SealJob): Promise<SealedCopy> {
  const key = random(INVITE_KEY_BYTES);
  const place = { share: job.share, item: job.item, version: job.version };
  const bytes = await sealCopy(subtle, key, place, job.plain, random(12));
  const wraps: Uint8Array<ArrayBuffer>[] = [];
  // Fix-next 85, DI4′: each wrap bound to the sealed bytes, so no co-holder's re-sealing opens for another.
  for (const holder of job.holders) wraps.push(await wrapCopyKey(subtle, holder.key, holder.invite, place, key, random(12), bytes));
  return { bytes, wraps };
}

/** Whether `value` is a plain object with exactly `fields`. */
function hasFields(value: unknown, fields: readonly string[]): value is object {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const keys = Object.keys(value).sort();
  const wanted = [...fields].sort();
  return keys.length === wanted.length && keys.every((key, i) => key === wanted[i]);
}

/** Bytes of exactly `length` (any length when null, up to `most`), held in their own buffer; null otherwise. */
function bytesOf(value: unknown, length: number | null, most = MAX_PLAIN): Uint8Array<ArrayBuffer> | null {
  if (!(value instanceof Uint8Array)) return null;
  if (length === null ? value.length > most : value.length !== length) return null;
  return new Uint8Array(value);
}

/** A job, read in the worker; null for anything that isn't one. */
export function readSealJob(data: unknown): SealJob | null {
  if (!hasFields(data, ['kind', 'share', 'item', 'version', 'plain', 'holders']) || Reflect.get(data, 'kind') !== 'seal') return null;
  const share = bytesOf(Reflect.get(data, 'share'), INVITE_ID_BYTES);
  const item: unknown = Reflect.get(data, 'item');
  const version = bytesOf(Reflect.get(data, 'version'), COPY_VERSION_BYTES);
  const plain = bytesOf(Reflect.get(data, 'plain'), null);
  const raw: unknown = Reflect.get(data, 'holders');
  if (share === null || typeof item !== 'string' || version === null || plain === null || !Array.isArray(raw) || raw.length === 0 || raw.length > MAX_HOLDERS) return null;
  const holders = raw.map((holder: unknown) => {
    if (!hasFields(holder, ['invite', 'key'])) return null;
    const invite = bytesOf(Reflect.get(holder, 'invite'), INVITE_ID_BYTES);
    const key = bytesOf(Reflect.get(holder, 'key'), INVITE_KEY_BYTES);
    return invite === null || key === null ? null : { invite, key };
  });
  return holders.every((holder) => holder !== null) ? { kind: 'seal', share, item, version, plain, holders } : null;
}

/** The worker's answer, read on the page for a job of `holders` holders; null for anything that isn't one. */
export function readSealAnswer(data: unknown, holders: number): SealAnswer | null {
  if (hasFields(data, ['kind']) && Reflect.get(data, 'kind') === 'failed') return { kind: 'failed' };
  if (!hasFields(data, ['kind', 'copy']) || Reflect.get(data, 'kind') !== 'sealed') return null;
  const copy: unknown = Reflect.get(data, 'copy');
  if (!hasFields(copy, ['bytes', 'wraps'])) return null;
  const bytes = bytesOf(Reflect.get(copy, 'bytes'), null, MAX_PLAIN + COPY_SEAL_BYTES);
  const raw: unknown = Reflect.get(copy, 'wraps');
  if (bytes === null || !Array.isArray(raw) || raw.length !== holders) return null;
  const wraps = raw.map((wrap: unknown) => bytesOf(wrap, COPY_WRAP_BYTES));
  return wraps.every((wrap) => wrap !== null) ? { kind: 'sealed', copy: { bytes, wraps } } : null;
}

/** The worker's global, as far as sealing goes (`self` in a worker). */
export interface SealScope {
  addEventListener(type: 'message', listener: (event: { readonly data: unknown }) => void): void;
  postMessage(message: SealAnswer): void;
}

/** The answer to one message: the job sealed, or `failed` for a message that isn't a job or a sealing that throws. */
export async function answerSeal(data: unknown, subtle: SubtleCrypto, random: Random): Promise<SealAnswer> {
  const job = readSealJob(data);
  if (job === null) return { kind: 'failed' };
  try {
    return { kind: 'sealed', copy: await sealItem(subtle, random, job) };
  } catch {
    return { kind: 'failed' };
  }
}

/** Serves sealings on `scope`, each answered once (the page ends the worker after the answer). */
export function runSealWorker(scope: SealScope, subtle: SubtleCrypto, random: Random): void {
  scope.addEventListener('message', (event) => {
    void answerSeal(event.data, subtle, random).then((message) => {
      scope.postMessage(message);
    });
  });
}

/** A sealing worker, as far as the page drives it (a `Worker`). */
export interface SealWorker {
  addEventListener(type: 'message' | 'messageerror' | 'error', listener: (event: unknown) => void): void;
  postMessage(message: SealJob): void;
  terminate(): void;
}

/** A function starting a sealing worker from its bundle under the app's Trusted Types policy (a module worker). */
export function sealWorker(source: WorkerSource<SealWorker>): () => SealWorker {
  return () => new source.Worker(source.trusted(source.url), { type: 'module', name: 'seal' });
}

/** A message event's data; undefined for anything else. */
function dataOf(event: unknown): unknown {
  return typeof event === 'object' && event !== null ? Reflect.get(event, 'data') : undefined;
}

/**
 * Runs `job` in a worker from `start` and ends it, whatever the answer: the sealed item, or null when the worker can't
 * start or be sent the job, reports an error, answers `failed` or answers what doesn't read. Never rejects.
 */
export function sealInWorker(start: () => SealWorker, job: SealJob): Promise<SealedCopy | null> {
  return new Promise((resolve) => {
    let worker: SealWorker;
    try {
      worker = start();
    } catch {
      resolve(null);
      return;
    }
    let settled = false;
    const settle = (copy: SealedCopy | null): void => {
      if (settled) return;
      settled = true;
      worker.terminate();
      resolve(copy);
    };
    worker.addEventListener('message', (event) => {
      const answer = readSealAnswer(dataOf(event), job.holders.length);
      settle(answer?.kind === 'sealed' ? answer.copy : null);
    });
    for (const type of ['error', 'messageerror'] as const) {
      worker.addEventListener(type, () => {
        settle(null);
      });
    }
    try {
      worker.postMessage(job);
    } catch {
      settle(null);
    }
  });
}
